import React from 'react';
import { ArrowRight, ArrowUpRight, BarChart3, CheckCircle2, ExternalLink, Gauge, Play, ShieldAlert, Sparkles } from 'lucide-react';
import { evalModalities, evalSourceBySlug, evalSources, type EvalLaunch, type EvalSource } from './evalData';

const surfaceStyle: Record<EvalLaunch['surface'], string> = {
  Gateway: 'border-cyan-300/20 bg-cyan-300/10 text-cyan-100',
  Comfy: 'border-fuchsia-300/20 bg-fuchsia-300/10 text-fuchsia-100',
  Agent: 'border-amber-300/20 bg-amber-300/10 text-amber-100',
  Evals: 'border-emerald-300/20 bg-emerald-300/10 text-emerald-100',
};

const SourceCard: React.FC<{ source: EvalSource }> = ({ source }) => (
  <a
    href={`/evals/${source.slug}`}
    className="group flex min-h-64 flex-col rounded-2xl border border-white/10 bg-white/[0.025] p-6 transition hover:-translate-y-0.5 hover:border-white/25 hover:bg-white/[0.045]"
  >
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/35">{source.eyebrow}</p>
        <h2 className="mt-2 text-xl font-bold tracking-tight text-white">{source.name}</h2>
      </div>
      <ArrowUpRight className="h-5 w-5 text-white/20 transition group-hover:text-white/70" />
    </div>
    <p className="mt-3 text-sm leading-6 text-white/55">{source.description}</p>
    <div className="mt-4 flex flex-wrap gap-1.5">
      {source.modalities.map((modality) => (
        <span key={modality} className="rounded-full border border-white/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white/45">
          {evalModalities.find((item) => item.id === modality)?.label}
        </span>
      ))}
    </div>
    <div className="mt-auto flex items-center gap-2 pt-6 text-xs font-semibold text-white/75">
      {source.launches.length} runnable paths <ArrowRight className="h-3.5 w-3.5" />
    </div>
  </a>
);

const LaunchCard: React.FC<{ launch: EvalLaunch }> = ({ launch }) => (
  <a href={launch.href} className="group flex flex-col rounded-2xl border border-white/10 bg-black/25 p-5 transition hover:border-white/25 hover:bg-white/[0.04]">
    <div className="flex items-center justify-between gap-3">
      <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${surfaceStyle[launch.surface]}`}>
        {launch.surface}
      </span>
      <Play className="h-4 w-4 text-white/25 transition group-hover:text-white/75" />
    </div>
    <h3 className="mt-4 text-base font-bold text-white">{launch.title}</h3>
    <code className="mt-1 text-[11px] text-white/35">{launch.model}</code>
    <p className="mt-3 text-sm leading-6 text-white/55">{launch.description}</p>
    <div className="mt-auto flex flex-wrap gap-1.5 pt-4">
      {launch.tags.map((tag) => <span key={tag} className="text-[10px] text-white/35">#{tag.replaceAll(' ', '-')}</span>)}
    </div>
  </a>
);

const Method: React.FC<{ number: string; title: string; text: string }> = ({ number, title, text }) => (
  <div className="relative border-l border-white/10 pb-8 pl-8 last:border-transparent last:pb-0">
    <span className="absolute -left-3.5 top-0 grid h-7 w-7 place-items-center rounded-full border border-white/15 bg-black text-[10px] font-bold text-white/60">{number}</span>
    <h3 className="font-semibold text-white">{title}</h3>
    <p className="mt-1 text-sm leading-6 text-white/50">{text}</p>
  </div>
);

const EvalHub: React.FC = () => (
  <main className="overflow-hidden px-4 pb-24 pt-28 sm:px-6">
    <section className="relative mx-auto max-w-6xl">
      <div className="pointer-events-none absolute -left-32 -top-40 h-96 w-96 rounded-full bg-cyan-500/10 blur-[100px]" />
      <div className="pointer-events-none absolute right-0 top-10 h-80 w-80 rounded-full bg-fuchsia-500/10 blur-[110px]" />
      <header className="relative max-w-4xl">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.035] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/55">
          <BarChart3 className="h-3.5 w-3.5" /> Model evals → working inference
        </div>
        <h1 className="mt-6 text-4xl font-bold tracking-[-0.04em] text-white sm:text-6xl">
          A leaderboard is a lead,<br /><span className="text-white/45">not the decision.</span>
        </h1>
        <p className="mt-6 max-w-3xl text-lg font-light leading-8 text-white/60">
          Read independent evidence from Artificial Analysis and other maintained evals, open the exact models we host, then verify the shortlist on your prompts. From public benchmark to production usage.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#sources" className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-bold text-black">Explore sources <ArrowRight className="h-4 w-4" /></a>
          <a href="/tools/bulk-evals" className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/[0.035] px-4 py-2.5 text-sm font-semibold text-white">Run your eval <Gauge className="h-4 w-4" /></a>
        </div>
      </header>

      <section className="relative mt-16 grid overflow-hidden rounded-2xl border border-white/10 bg-white/[0.025] md:grid-cols-4">
        {[
          ['01', 'Find evidence', 'Use live sources, methodology, and uncertainty—not copied score snapshots.'],
          ['02', 'Shortlist models', 'Match the benchmark modality and task distribution to the work you have.'],
          ['03', 'Run the model', 'Open hosted gateway inference, a cloud agent, or a tested Comfy GPU workflow.'],
          ['04', 'Keep the regression', 'Turn representative prompts and failures into a repeatable acceptance suite.'],
        ].map(([n, title, text], index) => (
          <div key={n} className={`p-6 ${index ? 'border-t border-white/10 md:border-l md:border-t-0' : ''}`}>
            <p className="font-mono text-xs text-emerald-300/70">{n}</p>
            <h2 className="mt-3 font-bold text-white">{title}</h2>
            <p className="mt-2 text-sm leading-6 text-white/45">{text}</p>
          </div>
        ))}
      </section>

      <section id="sources" className="relative mt-20 scroll-mt-24">
        <div className="mb-7 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Independent evidence</p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-white">Sources and methodology</h2>
          </div>
          <p className="max-w-md text-sm leading-6 text-white/45">Every source below links to its live result and methodology. app.nz does not present old leaderboard values as current facts.</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {evalSources.map((source) => <SourceCard key={source.slug} source={source} />)}
        </div>
      </section>

      <section className="mt-20 grid gap-8 rounded-3xl border border-white/10 bg-gradient-to-br from-emerald-400/[0.08] to-cyan-400/[0.03] p-7 md:grid-cols-[0.8fr_1.2fr] md:p-10">
        <div>
          <Sparkles className="h-5 w-5 text-emerald-300" />
          <h2 className="mt-4 text-2xl font-bold text-white">The useful eval loop</h2>
          <p className="mt-3 text-sm leading-6 text-white/55">Public benchmarks are discovery data. Your acceptance suite is deployment data. Keep both, and record model version, provider route, prompt, settings, latency, cost, and failures.</p>
          <a href="/blog/from-leaderboards-to-production-evals" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-emerald-200">Read the field guide <ArrowRight className="h-4 w-4" /></a>
        </div>
        <div>
          <Method number="1" title="Start wide" text="Use multiple source types: objective tasks, human preference, agentic work, speed, and price." />
          <Method number="2" title="Run representative work" text="Include normal inputs, hard inputs, policy edges, and known failure cases from production." />
          <Method number="3" title="Choose cost per accepted output" text="Cheapest per token or GPU-second is irrelevant if retries erase the saving." />
          <Method number="4" title="Re-run on change" text="Model aliases, provider behavior, workflows, and prompts all change; regression data makes the change visible." />
        </div>
      </section>
    </section>
  </main>
);

const EvalDetail: React.FC<{ source: EvalSource }> = ({ source }) => (
  <main className="px-4 pb-24 pt-28 sm:px-6">
    <div className="mx-auto max-w-6xl">
      <a href="/evals" className="inline-flex items-center gap-2 text-sm font-semibold text-white/45 hover:text-white"><ArrowRight className="h-4 w-4 rotate-180" /> All eval sources</a>
      <header className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px] lg:items-end">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300/70">{source.operator} · {source.eyebrow}</p>
          <h1 className="mt-3 text-4xl font-bold tracking-[-0.04em] text-white sm:text-6xl">{source.name}</h1>
          <p className="mt-5 max-w-3xl text-lg font-light leading-8 text-white/60">{source.description}</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row lg:flex-col">
          <a href={source.href} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-bold text-black">Open live leaderboard <ExternalLink className="h-4 w-4" /></a>
          <a href={source.methodologyHref} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/[0.03] px-4 py-2.5 text-sm font-semibold text-white">Read methodology <ExternalLink className="h-4 w-4" /></a>
        </div>
      </header>

      <section className="mt-12 grid gap-4 lg:grid-cols-[1fr_1fr_1fr]">
        <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-6">
          <CheckCircle2 className="h-5 w-5 text-emerald-300" />
          <h2 className="mt-4 font-bold text-white">What it measures</h2>
          <ul className="mt-3 space-y-3 text-sm leading-6 text-white/55">{source.measures.map((item) => <li key={item} className="flex gap-2"><span className="text-emerald-300/70">•</span>{item}</li>)}</ul>
        </div>
        <div className="rounded-2xl border border-cyan-300/10 bg-cyan-300/[0.035] p-6">
          <Gauge className="h-5 w-5 text-cyan-300" />
          <h2 className="mt-4 font-bold text-white">Use it for</h2>
          <p className="mt-3 text-sm leading-6 text-white/55">{source.usefulFor}</p>
          <p className="mt-5 border-t border-white/10 pt-4 text-xs leading-5 text-white/35">{source.updateNote}</p>
        </div>
        <div className="rounded-2xl border border-amber-300/10 bg-amber-300/[0.035] p-6">
          <ShieldAlert className="h-5 w-5 text-amber-300" />
          <h2 className="mt-4 font-bold text-white">Read with care</h2>
          <p className="mt-3 text-sm leading-6 text-white/55">{source.caution}</p>
        </div>
      </section>

      <section className="mt-16">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">From evidence to usage</p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-white">Run the closest available path</h2>
          </div>
          <p className="max-w-md text-sm leading-6 text-white/45">These are availability mappings, not claims that an app.nz route reproduces the source’s score or harness.</p>
        </div>
        <div className="mt-7 grid gap-4 md:grid-cols-2 lg:grid-cols-3">{source.launches.map((launch) => <LaunchCard key={launch.title} launch={launch} />)}</div>
      </section>

      <section className="mt-16 rounded-2xl border border-white/10 bg-white/[0.025] p-7 sm:flex sm:items-center sm:justify-between sm:gap-8">
        <div>
          <h2 className="text-xl font-bold text-white">Make the final decision on your data</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-white/50">Keep public evidence as context, then score representative inputs with a frozen rubric. Inspect failures—not only the mean.</p>
        </div>
        <a href="/tools/bulk-evals" className="mt-5 inline-flex shrink-0 items-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-bold text-black sm:mt-0">Build an eval set <ArrowRight className="h-4 w-4" /></a>
      </section>
    </div>
  </main>
);

export const EvalsPage: React.FC<{ slug?: string }> = ({ slug }) => {
  if (!slug) return <EvalHub />;
  const source = evalSourceBySlug[slug];
  if (!source) return <EvalHub />;
  return <EvalDetail source={source} />;
};
